/*
 * This code is protected by intellectual property rights.
 * Dr. Ing. h.c. F. Porsche AG owns exclusive rights of use.
 * © 2017-2026, Dr. Ing. h.c. F. Porsche AG.
 */

import {
  booleanAttribute,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  input,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  signal,
  ViewChildren,
  ChangeDetectionStrategy,
} from '@angular/core'
import { MyProfileListMode } from '@myprofile-ui/myprofile-phone-utils'
import { PidxScrollToService, WINDOW } from '@myprofile-ui/pidx-utils'
import { Actions, Store, ofActionSuccessful } from '@ngxs/store'
import { EMPTY, Observable, Subject, combineLatest, skip, mergeMap } from 'rxjs'
import { catchError, pairwise, startWith, take, takeUntil } from 'rxjs/operators'
import { MyProfilePhoneEditFormComponent } from './components/myprofile-phone-editform/myprofile-phone-editform.component'
import { MyProfileWidgetMode } from './enums/myprofile-widget-mode'
import { DeletePhone, ShowBlankEditor } from './store/editor.actions'
import { GetPhones, GetProfile, SelectPhone, SetListOverlayLoadingStatus } from './store/list.actions'
import {
  GetMfaInformation,
  GetTranslations,
  SetAppErrorMessage,
  SetAppLoadingStatus,
  SetEditorErrorMessage,
  SetInitialConfiguration,
} from './store/phone-widget.actions'
import { RESTRICT_GLOBAL_UI_CHANGES } from '../../../pidx-utils/src/lib/helpers/restrictGlobalUIChanges.injectionToken'
import { PhoneCommunicationDto } from '../../../../apps/myprofile-ui/src/app/services/phone/phone.service'

@Component({
  selector: 'myprofile-profile-phone-widget-ng',
  templateUrl: './myprofile-profile-phone-widget.component.html',
  host: { class: 'profile:block profile:relative profile:break-words' },
  standalone: false,
  // Angular 22 changed the implicit default to OnPush (see angular/angular#67687). This component was never migrated
  // to OnPush semantics, so Eager restores the pre-v22 behaviour it relies on.
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class MyProfileProfilePhoneWidgetComponent implements OnInit, OnDestroy {
  private window = inject<Window>(WINDOW)
  private _scrollToService = inject(PidxScrollToService)
  private store = inject(Store)
  private actions$ = inject(Actions)

  private readonly MFA_INFO_DISMISSED_KEY = 'myprofile-phone-widget.mfaInfoDismissed'

  @Input() mode: MyProfileWidgetMode = MyProfileWidgetMode.PROFILE

  @Input() locale!: string
  @Input() country!: string

  @Input() scrollOnAddNew = false
  @Input() token?: string // Bearer-Token for BFF communication
  @Input() shouldShowHeadline = false
  @Input() initiallySelectedPhoneId?: string
  @Input() hasConfirmedVehicle = false

  /**
   * When `true`, the widget announces its own initial-load status via a polite
   * live region using its bundled translation pipeline (translated key
   * `phoneWidget.general.loadingStatus`). Default `false` keeps the widget
   * silent during initial load so a host page can orchestrate one global
   * announcement; set to `true` for standalone embeddings.
   *
   * Does not gate the saving/verification overlays (always announced because
   * they are user-initiated).
   */
  public announceLoading = input(false, { transform: booleanAttribute })
  public mfaInfoActive = signal<boolean>(this.window.localStorage.getItem(this.MFA_INFO_DISMISSED_KEY) !== 'true')

  @Output() phoneSelected = new EventEmitter<string>()
  @Output() phonesStateChanged = new EventEmitter()
  @Output() newPhoneVerified = new EventEmitter()
  @Output() goToPorscheIdChangePageEvent = new EventEmitter()

  phones$: Observable<PhoneCommunicationDto[]> = inject(Store).select(({ phoneWidget }) => phoneWidget.phones)
  isLoading$: Observable<boolean> = inject(Store).select(({ phoneWidget }) => phoneWidget.loading)
  phoneIdBeingEdited$: Observable<string> = inject(Store).select(({ phoneWidget }) => phoneWidget.phoneIdBeingEdited)
  tmpPhone$: Observable<PhoneCommunicationDto> = inject(Store).select(({ phoneWidget }) => phoneWidget.editor.tmpPhone)
  errorMessage$: Observable<string> = inject(Store).select(({ phoneWidget }) => phoneWidget.errorMessage)
  selectedPhoneId$: Observable<string> = inject(Store).select(({ phoneWidget }) => phoneWidget.phoneList.selectedPhoneId)
  phoneNumberBeeingVerified$: Observable<string> = inject(Store).select(({ phoneWidget }) => phoneWidget.phones.phoneNumberBeingVerified)
  isCnUser$: Observable<boolean> = inject(Store).select(({ phoneWidget }) => phoneWidget.isCnUser)
  editorVisible$: Observable<boolean> = inject(Store).select(({ phoneWidget }) => phoneWidget.editor.visible)
  mfaSmsEnrolled$: Observable<boolean> = inject(Store).select(({ phoneWidget }) => phoneWidget.mfaSmsEnrolled)

  @ViewChildren(MyProfilePhoneEditFormComponent, { read: ElementRef }) private phoneEditFormQL!: QueryList<ElementRef>

  // enums to use in template
  MyProfileWidgetMode = MyProfileWidgetMode
  MyProfileListMode = MyProfileListMode
  public errorWhileInitializing = false
  public restrictGlobalUIChanges = inject<boolean>(RESTRICT_GLOBAL_UI_CHANGES)
  private unsubscribe$ = new Subject<void>()
  private maxPhones = 5

  /** Page-level fallback id (e.g. completion-status CTA) used when no in-widget add button is rendered. */
  private externalFocusReturnId: string | null = null

  async ngOnInit() {
    this.actions$.pipe(ofActionSuccessful(SelectPhone), takeUntil(this.unsubscribe$)).subscribe(({ selectedPhoneId }) => {
      this.phoneSelected.emit(selectedPhoneId)
    })

    // List-row delete doesn't set phoneIdBeingEdited, so the editor-close subscription
    // (editorVisible$ true→false) doesn't fire. Restore focus directly here so the
    // delete-from-list flow lands on #add-phone-number-button (WCAG 2.4.3). For editor-
    // initiated deletes, the editor also closes and triggers the same helper, which
    // simply re-focuses the same element — harmless.
    this.actions$.pipe(ofActionSuccessful(DeletePhone), takeUntil(this.unsubscribe$)).subscribe(() => {
      this.restoreFocusAfterLeavingEditor()
    })

    this.store
      .dispatch(new SetInitialConfiguration(this.mode, this.country, this.locale, this.token ?? ''))
      .pipe(
        take(1),
        takeUntil(this.unsubscribe$),
        mergeMap(() => {
          return combineLatest([
            this.store.dispatch(new GetPhones()),
            this.store.dispatch(new GetProfile()),
            this.store.dispatch(new GetTranslations()),
            this.store.dispatch(new GetMfaInformation()),
          ]).pipe(
            catchError(() => {
              this.errorWhileInitializing = true
              return EMPTY
            }),
          )
        }),
      )
      .subscribe(([{ phoneWidget }]) => {
        let initiallySelectedPhone = null
        if (this.initiallySelectedPhoneId) {
          initiallySelectedPhone = phoneWidget.phones.find((phone: PhoneCommunicationDto) => phone.id === this.initiallySelectedPhoneId)
        }
        if (initiallySelectedPhone) {
          this.store.dispatch(new SelectPhone(initiallySelectedPhone.id))
        } else {
          const standardPhone = phoneWidget.phones.find((phone: PhoneCommunicationDto) => phone.isStandard)
          if (standardPhone) {
            this.store.dispatch(new SelectPhone(standardPhone.id))
          }
        }

        this.store.dispatch(new SetAppLoadingStatus(false))
        this.store.dispatch(new SetListOverlayLoadingStatus(false))
      })

    this.phones$.pipe(skip(1), takeUntil(this.unsubscribe$)).subscribe(() => {
      this.phonesStateChanged.emit()
    })
    this.phoneNumberBeeingVerified$.subscribe((value) => this.newPhoneVerified.emit())

    // Restore focus when the editor closes (visible -> hidden transition). Mirrors the birthday
    // widget's chain pattern: prefer the in-widget add button if rendered, fall back to the page-level CTA.
    // TODO(focus-fallback): extract this "focus first id that resolves" into a shared pidx-utils helper
    // once all five widgets converge on the same shape.
    this.editorVisible$.pipe(startWith(false), pairwise(), takeUntil(this.unsubscribe$)).subscribe(([wasVisible, isVisible]) => {
      if (wasVisible && !isVisible) {
        this.restoreFocusAfterLeavingEditor()
      }
    })
  }

  enterAddPhoneMode(externalFocusReturnId?: string) {
    if (externalFocusReturnId) {
      this.externalFocusReturnId = externalFocusReturnId
    }
    this.store.dispatch(new SetAppErrorMessage(null))
    this.store.dispatch(new SetEditorErrorMessage(null))
    this.store.dispatch(new ShowBlankEditor(true))
    if (this.phoneEditFormQL) {
      this.phoneEditFormQL.changes.pipe(take(1)).subscribe((list) => {
        this.window.setTimeout(() => {
          const host = list.first?.nativeElement as HTMLElement | undefined
          if (!host) {
            return
          }
          if (this.scrollOnAddNew) {
            this._scrollToService.scrollToElement(host, 24)
          }
          host.focus()
        }, 0)
      })
    }
  }

  isAddPhoneTemplate(isToggle = false): boolean {
    const phoneIdBeingEdited = this.store.selectSnapshot((state) => state.phoneWidget.phoneIdBeingEdited)
    const phoneNumberBeingVerified = this.store.selectSnapshot((state) => state.phoneWidget.phoneNumberBeingVerified)
    const showEditor = this.store.selectSnapshot((state) => state.phoneWidget.editor.visible)

    if (phoneIdBeingEdited || (phoneNumberBeingVerified && phoneNumberBeingVerified.number)) {
      return false
    }
    return isToggle ? showEditor : !showEditor
  }

  maxNumbersReached(): boolean {
    const phones = this.store.selectSnapshot((state) => state.phoneWidget.phones)
    return phones.length >= this.maxPhones
  }

  public ngOnDestroy(): void {
    this.unsubscribe$.next()
    this.unsubscribe$.complete()
  }

  public dismissMfaInfo(): void {
    this.mfaInfoActive.set(false)
    this.window.localStorage.setItem(this.MFA_INFO_DISMISSED_KEY, 'true')
  }

  public goToPorscheIdChangePage() {
    this.goToPorscheIdChangePageEvent.emit()
  }

  private restoreFocusAfterLeavingEditor() {
    const externalId = this.externalFocusReturnId
    this.externalFocusReturnId = null
    this.window.setTimeout(() => {
      const internal = document.getElementById('add-phone-number-button')
      if (internal) {
        internal.focus()
        return
      }
      if (externalId) {
        document.getElementById(externalId)?.focus()
      }
    }, 0)
  }
}
